import { getManureTypeNVzNitrogen } from "@omnia/shared/manure"
import { CropId, ICropping, IManureType, RegionId } from "../omnia"
import { IFertiliserAnalysis, IManureStock, INutritionPlanProductApp, ISoilTypeService, ManureAppMethod } from "../types"
import { checkLightOrShallowSoil } from "../utils"


export function getManureTypeApp(type: IManureType, method: ManureAppMethod, holdingRegion: RegionId) {

    if (!type) {
        return
    }

    const { Applications: apps } = type

    if (!apps) {
        return
    }

    function checkRegion(appRegion: RegionId) {

        switch (holdingRegion) {

            case RegionId.England:
            case RegionId.Wales:
                return (appRegion == holdingRegion) || (appRegion == null);

            case RegionId.Scotland:
                return (appRegion == holdingRegion)

            default:
                return false

        }
    }

    return apps.find(a => (a.Method === method) && checkRegion(a.Region))

}

export function getManureAnalysisProvider(soilTypes: ISoilTypeService, region: RegionId) {

    return function getManureAnalysis(stock: IManureStock, application?: INutritionPlanProductApp, cropping?: ICropping): IFertiliserAnalysis {

        var analysis: IFertiliserAnalysis = {};

        var stockAnalysis = stock.Custom ? stock : stock.ManureType;
        var nAvailable = getNAvailability(stock, application, cropping);

        analysis.N = (stockAnalysis.N / 1000) * nAvailable;
        analysis.NTotal = stockAnalysis.N / 1000;
        analysis.SO3 = stockAnalysis.SO3 / 1000;
        analysis.MgO = stockAnalysis.MgO / 1000;
        analysis.P2O5 = (stockAnalysis.P2O5 / 1000) * stockAnalysis.P2O5Available;
        analysis.K2O = (stockAnalysis.K2O / 1000) * stockAnalysis.K2OAvailable;

        // 210302 - The 3 properties below are not supported at manure stock level,
        // they can only be provided by the manure type
        // analysis.Liquid = stockAnalysis.Liquid;
        // analysis.LiquidUnit = stockAnalysis.LiquidUnit;
        // analysis.SpecificGravity = stockAnalysis.SpecificGravity;
        analysis.Liquid = stock.ManureType?.Liquid || false;
        analysis.LiquidUnit = stock.ManureType?.LiquidUnit;
        analysis.SpecificGravity = stock.ManureType?.SpecificGravity;

        return analysis;
    }

    function getNAvailability(manure: IManureStock, application: INutritionPlanProductApp, cropping: ICropping): number {

        let available = 0;

        if (cropping && application && application.ApplicationDate) {

            const date = getManureDate(application.ApplicationDate);
            const grass = !!cropping.GrassManagement;
            const osr = cropping.CropId === CropId.WinterOilseedRape;
            const lightAndShallow = checkLightOrShallowSoil(soilTypes, cropping)
            const method = application.Incorporation;
            const type = manure.NvzManureType || manure.ManureType;

            const app = getManureTypeApp(type, method, region)

            if (app) {

                if (grass && date.summer) {
                    available = app.NSummerGrass;
                } else if (date.autumn) {
                    available = lightAndShallow ? app.NJulOctLightSoils : app.NJulOctOtherSoils;
                } else if (date.spring) {
                    available = lightAndShallow ? app.NNovJanLightSoils : app.NNovJanOtherSoils;
                } else if (date.febApr) {
                    available = app.NFebApr;
                }

                if (date.autumn && grass && app.NIncreaseAutumnGrass) {
                    available += app.NIncreaseAutumnGrass;
                } else if (date.autumn && osr && app.NIncreaseAutumnOilSeedRape) {
                    available += app.NIncreaseAutumnOilSeedRape;
                }

            }

        }

        let max = available;

        const nvzAvailable = (
            manure.Custom
                ? manure.NNvzAvailable
                : getManureTypeNVzNitrogen(manure.ManureType, region)
        ) || 0;

        max = Math.max(available, nvzAvailable);

        if (manure.NAvailableMax) {
            max = Math.min(manure.NAvailableMax, max);
        }

        return max;

    }

}

function getManureDate(date: Date): ManureDate {

    if (monthAndDateBetween(date, dates.endJuly, dates.oct)) {
        return { autumn: true }
    }

    if (monthAndDateBetween(date, dates.nov, dates.jan)) {
        return { spring: true }
    }

    if (monthAndDateBetween(date, dates.feb, dates.apr)) {
        return { febApr: true }
    }

    if (monthAndDateBetween(date, dates.may, dates.midJuly)) {
        return { summer: true }
    }

    return {}
}

function monthAndDateBetween(initDate: Date, start: Date, end: Date): boolean {

    var date = new Date(initDate.getTime());
    if (date.getMonth() < 4) {
        date.setFullYear(2001);
    } else {
        date.setFullYear(2000);
    }

    return date >= start && date <= end;
}

const dates = {
    jan: new Date(2001, 1 - 1, 31),
    feb: new Date(2001, 2 - 1, 1),
    apr: new Date(2001, 4 - 1, 30),
    may: new Date(2000, 5 - 1, 1),
    midJuly: new Date(2000, 7 - 1, 14),
    endJuly: new Date(2000, 7 - 1, 15),
    oct: new Date(2000, 10 - 1, 31),
    nov: new Date(2000, 11 - 1, 1)
}

interface ManureDate {
    autumn?: boolean,
    spring?: boolean,
    febApr?: boolean,
    summer?: boolean
}
